import { type ReactElement, useMemo } from 'react';
import {
  i18nInit,
  useLanguageControlOverrides,
  useLoadLocale
} from '@elfsight/lib-i18n';
import { LOCAL_WIDGET_ID } from '@elfsight/lib-utils';
import {
  getVisibilityOnPageSettings,
  useDisplayInSchedule,
  VisibilityOnDevicesProvider,
  VisibilityOnPagesWrapper
} from '@elfsight/widget-conditional-display';
import { FormProvider } from '@elfsight/widget-form-builder/widget';
import { useCustomJS, useDefineAPISchema } from '@elfsight/widget-js-api';
import { PortalNestProvider } from '@elfsight/widget-modal';
import {
  CustomCSS,
  createCustomCSSPrefixClassName,
  EmbedRoot,
  useFontsLoader
} from '@elfsight/widget-ui';
import { StorageProvider, WidgetRectProvider } from '@elfsight/widget-utils';
import { I18nProvider } from '@lingui/react';
import { useSettings } from '@wu/widget-core';
import CATALOG from '../i18n/languages/widget/catalog.json';
import { meta } from './meta';
import type { SpinningWheelSettings } from './types';
import { FormControllerProvider } from './widget/form-controller-context';
import { Widget } from './widget/widget';

export function SpinningWheel() {
  return (
    <Providers>
      <Widget />
    </Providers>
  );
}

function Providers({ children }: { children: ReactElement }) {
  const settings = useSettings();
  const {
    customCSS,
    customJS,
    font,
    editMode,
    widgetId,
    renderThumbnailMode,
    textColor,
    language,
    visibilityOnDevices
  } = settings;
  const customCSSPrefixClassName = createCustomCSSPrefixClassName(
    'spinning-wheel',
    widgetId
  );

  useDefineAPISchema();
  useCustomJS(customJS, editMode || renderThumbnailMode);
  useFontsLoader([font]);

  const isVisibleBySchedule = useDisplayInSchedule({
    settings,
    enabled: settings.schedule
  });

  const i18n = useMemo(() => i18nInit(), []);
  const messages = useLoadLocale(language?.language, 'widget', i18n);
  useLanguageControlOverrides({ language, messages, catalog: CATALOG, i18n });

  if (!settings.editMode && !isVisibleBySchedule) return null;

  return (
    <I18nProvider i18n={i18n}>
      <VisibilityOnDevicesProvider
        widgetDisplayMode={visibilityOnDevices}
        editMode={editMode}
      >
        <VisibilityOnPagesWrapper
          widgetVisibilityOnPages={getVisibilityOnPageSettings<SpinningWheelSettings>(
            settings
          )}
          editMode={editMode}
        >
          <WidgetRectProvider>
            <StorageProvider widgetId={widgetId || ''} name={meta.appName}>
              <PortalNestProvider
                customCSSClassName={customCSSPrefixClassName}
                font={font}
              >
                <FormProvider widgetId={widgetId || LOCAL_WIDGET_ID}>
                  <FormControllerProvider>
                    <CustomCSS
                      customCSS={customCSS}
                      prefixClassName={customCSSPrefixClassName}
                    />

                    <EmbedRoot
                      customCSSClassName={customCSSPrefixClassName}
                      font={font}
                      textColor={textColor}
                    >
                      {children}
                    </EmbedRoot>
                  </FormControllerProvider>
                </FormProvider>
              </PortalNestProvider>
            </StorageProvider>
          </WidgetRectProvider>
        </VisibilityOnPagesWrapper>
      </VisibilityOnDevicesProvider>
    </I18nProvider>
  );
}
